import { useState, useTransition } from "react";
import { cn } from "../../utils";
import { WEB_ACCENTS } from "../../theme-accents";
import { SettingsSection } from "./settings-section";

export type Theme = "light" | "dark" | "system";

const COOKIE_KEY = "theme";
const COOKIE_MAX_AGE = 60 * 60 * 24 * 365;

function resolveBackground(theme: Theme): "light" | "dark" {
  if (theme !== "system") return theme;
  if (typeof window === "undefined") return "dark";
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

// Live-apply the (accent × background) pair to the document root, mirroring
// _chrome/theme-provider.tsx: the `.${accent}-${bg}` class drives --primary via
// globals.css (never a JS token write — see check-theme-token-guard), and the
// bare `${bg}` class is what the dark/light CSS keys off. The cookie carries the
// BACKGROUND only, as the SSR fast-path hint layout.tsx reads.
function applyAppearance(theme: Theme, accent: string) {
  if (typeof document === "undefined") return;
  const bg = resolveBackground(theme);
  const root = document.documentElement;
  Array.from(root.classList).forEach((c) => {
    if (c.includes("-dark") || c.includes("-light") || c === "light" || c === "dark") {
      root.classList.remove(c);
    }
  });
  root.classList.add(`${accent}-${bg}`, bg);
  document.cookie = `${COOKIE_KEY}=${bg}; path=/; max-age=${COOKIE_MAX_AGE}; samesite=lax`;
}
export type Density = "compact" | "comfortable" | "spacious";
export type Accent = string;

export interface AppearanceFormProps {
  initial: { theme: Theme; density: Density; accent: Accent };
  /** Server action — receives (kind, value). */
  onSave: (formData: FormData) => Promise<void>;
}

const THEME_OPTIONS: Array<{ value: Theme; label: string; hint: string }> = [
  { value: "light", label: "Light", hint: "Always light, regardless of system." },
  { value: "dark", label: "Dark", hint: "Always dark, regardless of system." },
  { value: "system", label: "System", hint: "Match your OS preference." },
];

const DENSITY_OPTIONS: Array<{ value: Density; label: string; hint: string }> = [
  { value: "compact", label: "Compact", hint: "Tighter spacing; more on screen." },
  { value: "comfortable", label: "Comfortable", hint: "Default spacing." },
  { value: "spacious", label: "Spacious", hint: "More breathing room." },
];

// Derived from the web accent registry (packages/ui/src/theme-accents.json) so
// the picker can never offer an accent the CSS doesn't define. The swatch shows
// the dark-mode fill (reads well on either canvas).
const ACCENT_OPTIONS: Array<{ value: Accent; label: string; swatch: string }> =
  WEB_ACCENTS.map((a) => ({ value: a.name, label: a.label, swatch: a.dark.primary }));

function SegmentedControl<T extends string>({
  options,
  value,
  onChange,
  pending,
}: {
  options: Array<{ value: T; label: string; hint?: string }>;
  value: T;
  onChange: (v: T) => void;
  pending: boolean;
}) {
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((o) => (
        // button-ignore: segmented option selector (theme/density swatch), not an action button
        <button
          key={o.value}
          type="button"
          disabled={pending}
          onClick={() => onChange(o.value)}
          className={cn(
            "rounded-md border px-3 py-1.5 type-body tracking-tight transition-colors disabled:opacity-50",
            value === o.value
              ? "border-foreground bg-surface-3 text-foreground shadow-surface-2"
              : "border-border bg-card text-muted-foreground hover:text-foreground hover:bg-accent/40",
          )}
        >
          <span className="block">{o.label}</span>
          {o.hint ? (
            <span className="block type-body text-muted-foreground/80">
              {o.hint}
            </span>
          ) : null}
        </button>
      ))}
    </div>
  );
}

export function AppearanceForm({ initial, onSave }: AppearanceFormProps) {
  const [theme, setThemeState] = useState<Theme>(initial.theme);
  const [density, setDensityState] = useState<Density>(initial.density);
  const [accent, setAccentState] = useState<Accent>(initial.accent);
  const [pending, startTransition] = useTransition();

  function commit(next: { theme: Theme; density: Density; accent: Accent }) {
    // Live-apply for the local UI (the accent × background class pair).
    applyAppearance(next.theme, next.accent);
    if (typeof document !== "undefined") {
      document.documentElement.dataset.density = next.density;
    }
    const fd = new FormData();
    fd.set("theme", next.theme);
    fd.set("density", next.density);
    fd.set("accent", next.accent);
    startTransition(async () => {
      await onSave(fd);
    });
  }

  return (
    <div className="space-y-6">
      <SettingsSection
        id="theme"
        title="Theme"
        description="Light, dark, or follow the system setting."
      >
        <SegmentedControl
          options={THEME_OPTIONS}
          value={theme}
          onChange={(v) => {
            setThemeState(v);
            commit({ theme: v, density, accent });
          }}
          pending={pending}
        />
      </SettingsSection>

      <SettingsSection
        id="density"
        title="Density"
        description="Spacing scale across the app."
      >
        <SegmentedControl
          options={DENSITY_OPTIONS}
          value={density}
          onChange={(v) => {
            setDensityState(v);
            commit({ theme, density: v, accent });
          }}
          pending={pending}
        />
      </SettingsSection>

      <SettingsSection
        id="accent"
        title="Accent"
        description="Highlight color for selected states and chrome details."
      >
        <div className="flex flex-wrap gap-2">
            {ACCENT_OPTIONS.map((o) => (
              // button-ignore: accent-color swatch selector, not an action button
              <button
                key={o.value}
                type="button"
                disabled={pending}
                onClick={() => {
                  setAccentState(o.value);
                  commit({ theme, density, accent: o.value });
                }}
                className={cn(
                  "flex items-center gap-2 rounded-md border px-3 py-1.5 type-body tracking-tight transition-colors disabled:opacity-50",
                  accent === o.value
                    ? "border-foreground bg-surface-3 text-foreground shadow-surface-2"
                    : "border-border bg-card text-muted-foreground hover:text-foreground hover:bg-accent/40",
                )}
              >
                <span
                  className="h-3.5 w-3.5 rounded-full ring-1 ring-inset ring-black/10"
                  style={{ backgroundColor: o.swatch }}
                />
                {o.label}
              </button>
            ))}
          </div>
        {pending ? (
          <span className="mt-3 inline-block type-body text-muted-foreground">
            Saving…
          </span>
        ) : null}
      </SettingsSection>
    </div>
  );
}
